import { createSlice } from '@reduxjs/toolkit';
import type { AsyncThunk } from '@reduxjs/toolkit';

import {
  fetchBillingBalance,
  fetchBillingBatches,
  fetchBillingInvoices,
  fetchBillingLedger,
  fetchBillingPaymentMethods,
  fetchBillingRateCard
} from './service';

const emptyResource = <T>(): IBillingResource<T> => ({ data: null, loading: false, error: false });

export const initialState: IBillingState = {
  balance: emptyResource<IBillingBalance>(),
  batches: emptyResource<IBillingBatch[]>(),
  ledger: { ...emptyResource<IBillingLedgerEntry[]>(), nextBefore: null },
  invoices: emptyResource<IBillingInvoiceSummary[]>(),
  paymentMethods: emptyResource<IBillingPaymentMethod[]>(),
  rateCard: emptyResource<IBillingRateCard>()
};

type ResourceKey = 'balance' | 'batches' | 'invoices' | 'paymentMethods' | 'rateCard';

const slice = createSlice({
  name: 'billing',
  initialState,
  reducers: {
    resetBillingState: () => initialState
  },
  extraReducers: (builder) => {
    /**
     * `AsyncThunk`'s own generics for the argument and the thunk API are unconstrained — RTK types
     * them this way itself — so they stay loose here while the payload type `T` is what the cases
     * below actually rely on.
     */
    const registerResource = <T, TArg>(key: ResourceKey, thunk: AsyncThunk<T, TArg, object>) => {
      builder
        .addCase(thunk.pending, (state) => {
          (state[key] as IBillingResource<T>).loading = true;
          (state[key] as IBillingResource<T>).error = false;
        })
        .addCase(thunk.fulfilled, (state, action: { payload: T }) => {
          (state[key] as IBillingResource<T>).loading = false;
          (state[key] as IBillingResource<T>).data = action.payload;
        })
        .addCase(thunk.rejected, (state) => {
          (state[key] as IBillingResource<T>).loading = false;
          (state[key] as IBillingResource<T>).error = true;
        });
    };

    registerResource('balance', fetchBillingBalance);
    registerResource('batches', fetchBillingBatches);
    registerResource('invoices', fetchBillingInvoices);
    registerResource('paymentMethods', fetchBillingPaymentMethods);
    registerResource('rateCard', fetchBillingRateCard);

    builder
      .addCase(fetchBillingLedger.pending, (state) => {
        state.ledger.loading = true;
        state.ledger.error = false;
      })
      .addCase(fetchBillingLedger.fulfilled, (state, action) => {
        const { items, nextBefore, append } = action.payload;
        state.ledger.loading = false;
        state.ledger.data = append ? [...(state.ledger.data || []), ...items] : items;
        state.ledger.nextBefore = nextBefore;
      })
      .addCase(fetchBillingLedger.rejected, (state) => {
        state.ledger.loading = false;
        state.ledger.error = true;
      });
  }
});

export const { resetBillingState } = slice.actions;
export const billingReducer = slice.reducer;

export const selectBillingBalance = (state: IAppState) => state.billing.balance;
export const selectBillingBatches = (state: IAppState) => state.billing.batches;
export const selectBillingLedger = (state: IAppState) => state.billing.ledger;
export const selectBillingInvoices = (state: IAppState) => state.billing.invoices;
export const selectBillingPaymentMethods = (state: IAppState) => state.billing.paymentMethods;
export const selectBillingRateCard = (state: IAppState) => state.billing.rateCard;
